import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Main } from '@/components/layout/main'
import { PageHeader } from '@/components/layout/page-header'
import { CreditCard } from 'lucide-react'

import { superAdminService } from '@/api/super-admin'
import { Payment, PaymentListParams, PaymentStatus } from '@/types/super-admin'
import { ColumnDef } from '@tanstack/react-table'
import { DataTable } from '@/components/table'
import { DataTableColumnHeader } from '@/components/data-table-column-header'
import DataTableSkeleton from '@/components/ui/data-table-skeleton'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { useRouter, useSearch } from '@tanstack/react-router'
import { filterNonNullValues } from '@/lib/utils'
import { Coins, MoreHorizontal } from 'lucide-react'
import { timestamp_to_date } from '@/utils/time'

interface PaymentsTableParams {
  page: number
  page_size: number
  ordering: string | null
  search: string | null
}

export default function PaymentsManagement() {
  const { t } = useTranslation()
  const searchParams = useSearch({ strict: false })
  const [payments, setPayments] = useState<Payment[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<boolean>(false)
  const [pagesCnt, setPagesCnt] = useState<number>(0)
  const [params, setParams] = useState<PaymentsTableParams>({
    page: (searchParams as any)?.page || 0,
    page_size: (searchParams as any)?.page_size || 10,
    ordering: (searchParams as any)?.ordering || null,
    search: null,
  })
  const router = useRouter()

  // Sync params to router search params
  useEffect(() => {
    router.navigate({
      to: '.',
      search: filterNonNullValues({
        page: params.page,
        page_size: params.page_size,
        ordering: params.ordering,
        search: params.search,
      }),
      replace: true,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params])

  // Details Dialog State
  const [detailsOpen, setDetailsOpen] = useState<boolean>(false)
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null)

  const openDetails = async (id: string) => {
    try {
      const p = await superAdminService.getPayment(id)
      setSelectedPayment(p)
      setDetailsOpen(true)
    } catch (e) {
      // ignore for now, keep UX simple
    }
  }

  const priceDisplay = (cents?: number | null, currency?: string | null) => {
    if (cents == null) return '-'
    const amount = (cents / 100).toFixed(2)
    return currency ? `${amount} ${currency}` : `${amount}`
  }

  const statusBadgeVariant = (status: PaymentStatus) => {
    switch (status) {
      case 'succeeded': return 'default'
      case 'pending': return 'secondary'
      case 'failed': return 'destructive'
      default: return 'secondary'
    }
  }

  const onValidate = async (payment: Payment, status: PaymentStatus) => {
    try {
      await superAdminService.validatePayment(payment.id, { status })
      // Refresh list after validation
      setParams(prev => ({ ...prev }))
    } catch (e) {
      // silently ignore for now; could add toast
    }
  }

  const columns: ColumnDef<Payment>[] = useMemo(() => [
    {
      accessorKey: 'cabinet_name',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('cabinet') || 'Cabinet'} />
      ),
      cell: ({ row }) => (
        <div className='w-fit text-nowrap font-mono text-xs'>
          {row.original.cabinet_name}
        </div>
      ),
      enableSorting: true,
      enableHiding: false,
    },
    {
      accessorKey: 'method',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('method') || 'Method'} />
      ),
      cell: ({ row }) => (
        <div className='w-fit text-nowrap capitalize'>
          {row.original.method}
        </div>
      ),
      enableSorting: true,
      enableHiding: false,
    },
    {
      accessorKey: 'amount_cents',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('amount') || 'Amount'} />
      ),
      cell: ({ row }) => (
        <div className='flex items-center gap-2'>
          <Coins className='h-4 w-4 text-muted-foreground' />
          <span>{priceDisplay(row.original.amount_cents, row.original.currency)}</span>
        </div>
      ),
      enableSorting: true,
      enableHiding: false,
    },
    {
      accessorKey: 'status',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('status') || 'Status'} />
      ),
      cell: ({ row }) => (
        <Badge variant={statusBadgeVariant(row.original.status)}>
          {row.original.status}
        </Badge>
      ),
      enableSorting: true,
      enableHiding: false,
    },
    {
      accessorKey: 'created_at',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('created_at') || 'Created'} />
      ),
      cell: ({ row }) => (
        <div className='w-fit text-nowrap'>
          {row.original.created_at ? timestamp_to_date(Number(row.original.created_at)).toLocaleString() : '-'}
        </div>
      ),
      enableSorting: true,
      enableHiding: false,
    },
    {
      id: 'actions',
      header: () => null,
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant='ghost' className='h-8 w-8 p-0'>
              <span className='sr-only'>Open menu</span>
              <MoreHorizontal className='h-4 w-4' />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='end'>
            <DropdownMenuItem onClick={() => openDetails(row.original.id)}>
              {t('view_details') || 'View details'}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onValidate(row.original, 'succeeded')}
            >
              {t('mark_as_succeeded') || 'Mark as Succeeded'}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onValidate(row.original, 'failed')}>
              {t('mark_as_failed') || 'Mark as Failed'}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
      enableSorting: false,
      enableHiding: false,
      meta: { className: 'w-10 text-center' }
    },
  ], [t])

  // Fetch list
  useEffect(() => {
    const p: PaymentListParams = {
      page: params.page,
      page_size: params.page_size,
      ordering: params.ordering || undefined,
      search: params.search || undefined,
    }
    setLoading(true)
    superAdminService.getPayments(p)
      .then(resp => {
        setPayments(resp.results)
        setPagesCnt(Math.ceil(resp.count / params.page_size))
        setError(false)
      })
      .catch(() => {
        setError(true)
      })
      .finally(() => setLoading(false))
  }, [params])

  // Toolbar with search
  function PaymentsTableToolbar(
    { table: _table, params, setParams }: { table?: any; params: PaymentsTableParams; setParams: React.Dispatch<React.SetStateAction<PaymentsTableParams>> }
  ) {
    return (
      <div className='flex items-center gap-2'>
        <Input
          placeholder='Search payments...'
          value={params.search ?? ''}
          onChange={(e) => setParams((p) => ({ ...p, search: e.target.value || null }))}
          className='h-8 w-[200px]'
        />
      </div>
    )
  }

  return (
    <>
      <Main>
        <PageHeader
          leading={
            <span className="mt-1 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <CreditCard className="h-5 w-5" />
            </span>
          }
          title={t('payments') || 'Payments'}
          description={t('payments_list_desc') || 'Manage and validate cabinet payments.'}
        />
        <div className='-mx-4 flex-1 overflow-auto px-4 py-1 lg:flex-row lg:space-x-12 lg:space-y-0'>
          {
            loading ? <DataTableSkeleton columns={6} rows={6} />
              : error ?
                <div className='py-8 text-center'>
                  {t('table_load_error') || 'Error loading table'}
                </div>
                : <DataTable
                  data={payments}
                  columns={columns}
                  params={params}
                  setParams={setParams}
                  pages={pagesCnt}
                  ToolbarName={PaymentsTableToolbar}
                  storageKey="super-admin-payments-table"
                />
          }
        </div>
      </Main>

      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('payment_details') || 'Payment Details'}</DialogTitle>
          </DialogHeader>
          {!selectedPayment ? (
            <div className='py-4'>{t('loading') || 'Loading'}...</div>
          ) : (
            <div className='grid grid-cols-2 gap-4'>
              <div>
                <div className='text-sm text-muted-foreground'>{t('cabinet') || 'Cabinet'}</div>
                <div className='font-mono text-xs'>{selectedPayment.cabinet_name}</div>
              </div>
              <div>
                <div className='text-sm text-muted-foreground'>{t('method') || 'Method'}</div>
                <div className='capitalize'>{selectedPayment.method}</div>
              </div>
              <div>
                <div className='text-sm text-muted-foreground'>{t('amount') || 'Amount'}</div>
                <div>{priceDisplay(selectedPayment.amount_cents, selectedPayment.currency)}</div>
              </div>
              <div>
                <div className='text-sm text-muted-foreground'>{t('status') || 'Status'}</div>
                <Badge variant={statusBadgeVariant(selectedPayment.status)}>
                  {selectedPayment.status}
                </Badge>
              </div>
              <div>
                <div className='text-sm text-muted-foreground'>{t('created_at') || 'Created'}</div>
                <div>{selectedPayment.created_at ? timestamp_to_date(Number(selectedPayment.created_at)).toLocaleString() : '-'}</div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}